import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TuneIcon from '@mui/icons-material/Tune';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import { ACCENT_BLUE, SUCCESS_GREEN } from '../Assessments/Workspace/qcTheme';

export type ApproveState = 'ready' | 'saving' | 'approved' | 'blocked';

const ACCENT = ACCENT_BLUE;
const FINALIZE_ACCENT = SUCCESS_GREEN;

const KebabButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'menuOpen',
})<{ menuOpen: boolean }>(({ menuOpen }) => ({
  borderRadius: '8px',
  color: menuOpen ? ACCENT : 'rgba(0,0,0,0.54)',
  background: menuOpen ? 'rgba(25,118,210,0.1)' : 'transparent',
  transition: 'background 120ms ease, color 120ms ease',
  '&:hover': { background: 'rgba(25,118,210,0.08)', color: ACCENT },
}));

const ActionsMenu = styled(Menu)({
  '& .MuiPaper-root': {
    minWidth: '296px',
    maxWidth: '360px',
    borderRadius: '14px',
    border: '1px solid rgba(0,0,0,0.07)',
    boxShadow: '0 4px 6px -2px rgba(16,24,40,0.04), 0 14px 32px -6px rgba(16,24,40,0.16)',
  },
  '& .MuiList-root': { padding: '6px' },
  // Row styling lives on the menu so every item picks it up uniformly.
  '& .MuiMenuItem-root': {
    borderRadius: '8px',
    // Indented past the section labels so each group reads as a header with items under it.
    padding: '8px 12px 8px 22px',
    gap: '12px',
    alignItems: 'flex-start',
    fontSize: '0.875rem',
    lineHeight: 1.35,
    color: 'rgba(0,0,0,0.87)',
    transition: 'background 120ms ease',
    // The assessment link renders as an <a> — re-assert color across link states (CLAUDE.md global anchor colours).
    '&, &:link, &:visited, &:hover, &:focus, &:active': {
      color: 'rgba(0,0,0,0.87)',
      textDecoration: 'none',
    },
    '&:hover': {
      background: 'rgba(25,118,210,0.08)',
      '& .action-icon, & .MuiListItemIcon-root': { color: ACCENT },
    },
    '&.Mui-disabled': {
      opacity: 1,
      color: 'rgba(0,0,0,0.34)',
      '& .action-icon, & .MuiListItemIcon-root': { color: 'rgba(0,0,0,0.24)' },
    },
  },
  // Fixed width so icons line up whichever wrapper an item uses.
  '& .action-icon, & .MuiListItemIcon-root': {
    display: 'inline-flex',
    width: '20px',
    minWidth: '20px',
    flexShrink: 0,
    marginTop: '1px',
    color: 'rgba(0,0,0,0.5)',
    transition: 'color 120ms ease',
  },
  '& .MuiListItemText-root': { margin: 0 },
  '& .MuiListItemText-primary': { fontSize: '0.875rem', lineHeight: 1.35 },
});

const SectionLabel = styled('li')({
  listStyle: 'none',
  padding: '10px 10px 5px',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.4)',
  pointerEvents: 'none',
  '&:first-of-type': { paddingTop: '4px' },
});

const FinalizeItem = styled(MenuItem)({
  marginTop: '5px',
  color: FINALIZE_ACCENT,
  fontWeight: 500,
  '& .action-icon': { color: FINALIZE_ACCENT },
  '&:hover': {
    background: 'rgba(46,125,50,0.09)',
    '& .action-icon': { color: FINALIZE_ACCENT },
  },
});

// A record, not an action: finalized rows show their state here instead of a dead button.
const FinalizedStatus = styled('li')({
  listStyle: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  margin: '5px 0 0',
  padding: '8px 12px 8px 22px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: FINALIZE_ACCENT,
  '& .action-icon': { color: FINALIZE_ACCENT },
});

const Divider = styled('li')({
  listStyle: 'none',
  height: '1px',
  margin: '6px 4px 0',
  background: 'rgba(0,0,0,0.08)',
});

const Label = styled('span')({
  display: 'block',
  lineHeight: 1.35,
});

// Secondary line: what the action will actually do, or why it is unavailable.
const Hint = styled('span')({
  display: 'block',
  marginTop: '2px',
  fontSize: '0.6875rem',
  lineHeight: 1.35,
  color: 'rgba(0,0,0,0.5)',
});

const APPROVE_LABELS: Record<ApproveState, string> = {
  ready: 'Finalize Assessment QC',
  saving: 'Finalizing…',
  approved: 'Finalized',
  blocked: 'Finalize Assessment QC',
};

const AssessmentActionsCell = ({
  canEditQc = false,
  assessmentLink,
  onContinueReview,
  continueReviewDisabledReason,
  onQcRunDetails,
  onRunQc,
  runQcEligible = false,
  runQcIneligibleReason,
  runQcBusy = false,
  onPreviewRun,
  onEditScore,
  onPointManager,
  onApprove,
  approveState = 'ready',
  approveBlockedReason,
}: {
  // QC edits need the row's workflow; rows without one get the menu but no QC actions.
  canEditQc?: boolean;
  assessmentLink?: string;
  onContinueReview?: () => void;
  // Disabled while set; the reason doubles as the hint.
  continueReviewDisabledReason?: string;
  onQcRunDetails?: () => void;
  onRunQc?: () => void;
  runQcEligible?: boolean;
  runQcIneligibleReason?: string;
  runQcBusy?: boolean;
  onPreviewRun?: () => void;
  onEditScore?: () => void;
  onPointManager?: () => void;
  onApprove?: () => void;
  approveState?: ApproveState;
  approveBlockedReason?: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const run = (action?: () => void, disabled = false) => () => {
    if (disabled || !action) return;
    handleMenuClose();
    action();
  };

  const showReview = assessmentLink || (canEditQc && (onContinueReview || onQcRunDetails));
  // Preview is survey-scoped and writes nothing, so it stays available with no active workflow.
  const showRun = (canEditQc && onRunQc) || onPreviewRun;
  const continueReviewDisabled = !!continueReviewDisabledReason;

  return (
    <>
      <KebabButton size="small" menuOpen={open} onClick={handleMenuOpen} aria-label="Assessment actions">
        <MoreVertIcon fontSize="small" />
      </KebabButton>
      <ActionsMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showReview && <SectionLabel>Review</SectionLabel>}
        {assessmentLink && (
          <MenuItem
            onClick={() => {
              window.open(`${window.location.origin}${assessmentLink}`, '_blank');
              handleMenuClose();
            }}
          >
            <span className="action-icon"><OpenInNewIcon fontSize="small" /></span>
            <Label>Open Assessment</Label>
          </MenuItem>
        )}
        {canEditQc && onContinueReview && (
          <MenuItem
            disabled={continueReviewDisabled}
            onClick={run(onContinueReview, continueReviewDisabled)}
          >
            <span className="action-icon"><RateReviewIcon fontSize="small" /></span>
            <span>
              <Label>Continue Review</Label>
              {continueReviewDisabledReason && <Hint>{continueReviewDisabledReason}</Hint>}
            </span>
          </MenuItem>
        )}
        {canEditQc && onQcRunDetails && (
          <MenuItem onClick={run(onQcRunDetails)}>
            <span className="action-icon"><ManageSearchIcon fontSize="small" /></span>
            <Label>QC Run Details</Label>
          </MenuItem>
        )}

        {showRun && <SectionLabel>Run</SectionLabel>}
        {canEditQc && onRunQc && (
          <MenuItem
            disabled={!runQcEligible || runQcBusy}
            onClick={run(onRunQc, !runQcEligible || runQcBusy)}
          >
            <span className="action-icon"><PlayCircleOutlineIcon fontSize="small" /></span>
            <span>
              <Label>{runQcBusy ? 'Starting QC…' : 'Run QC'}</Label>
              <Hint>
                {runQcEligible
                  ? 'Drafts issues · emails when done'
                  : runQcIneligibleReason || 'Unavailable'}
              </Hint>
            </span>
          </MenuItem>
        )}
        {onPreviewRun && (
          <MenuItem onClick={run(onPreviewRun)}>
            <span className="action-icon"><ScienceOutlinedIcon fontSize="small" /></span>
            <span>
              <Label>Preview Run</Label>
              <Hint>Dry run · saves nothing</Hint>
            </span>
          </MenuItem>
        )}

        <SectionLabel>Manage</SectionLabel>
        {canEditQc && onEditScore && (
          <MenuItem onClick={run(onEditScore)}>
            <span className="action-icon"><EditIcon fontSize="small" /></span>
            <Label>Edit Score</Label>
          </MenuItem>
        )}
        {canEditQc && onPointManager && (
          <MenuItem onClick={run(onPointManager)}>
            <span className="action-icon"><TuneIcon fontSize="small" /></span>
            <Label>Point Manager</Label>
          </MenuItem>
        )}
        {onApprove && <Divider />}
        {onApprove && approveState === 'approved' && (
          <FinalizedStatus>
            <span className="action-icon"><CheckCircleOutlineIcon fontSize="small" /></span>
            <Label>{APPROVE_LABELS.approved}</Label>
          </FinalizedStatus>
        )}
        {onApprove && approveState !== 'approved' && (
          <FinalizeItem
            disabled={approveState !== 'ready'}
            onClick={run(onApprove, approveState !== 'ready')}
          >
            <span className="action-icon"><CheckCircleOutlineIcon fontSize="small" /></span>
            <span>
              <Label>{APPROVE_LABELS[approveState]}</Label>
              {approveState === 'blocked' && <Hint>{approveBlockedReason || 'Awaiting QC review'}</Hint>}
            </span>
          </FinalizeItem>
        )}
      </ActionsMenu>
    </>
  );
};

export default AssessmentActionsCell;
